/**
 * Arthaus Marketplace — Google Ads sink.
 *
 * Booted by snippets/google-ads.liquid, which loads gtag.js for THIS market's Google Ads
 * account only and publishes window.__ahGoogleAds ({ id, addToCart, currency, feedCountry }).
 *
 * This file adds no listeners to the page itself. It subscribes to the storefront event bus
 * that assets/posthog-events.js publishes (`ah:track` on window, replayed from
 * window.__ahTrackBuffer for events emitted before this file ran), so every analytics sink
 * — PostHog, $AXON, Google Ads — sees the same events from the same source:
 *
 *   artwork_view        → view_item             (dynamic remarketing)
 *   collection_view     → view_item_list        (dynamic remarketing)
 *   search_submit       → view_search_results   (dynamic remarketing)
 *   cart_lines_added    → add_to_cart           (remarketing + "Add to cart (theme)" conversion, secondary)
 *
 * `cart_lines_added` is the confirmed form of an add: emitted once per successful
 * /cart/add(.js) response with the lines Shopify actually created (presentment-currency
 * prices, real line properties). A gallery-wall set is one add with every piece in it.
 *
 * Item ids match the Merchant Center feed built by AMS (lib/feeds/render.js):
 *   ART-<artworkId>-framed | ART-<artworkId>-print   (v2 artwork lines: _artwork_id + _sku)
 *   SET-<setSlug>                                      (set lines: _set_slug, or a '<room>--<set>' _set_id)
 * Legacy (non-v2) products fall back to Shopify's own feed format shopify_<CC>_<pid>_<vid>.
 *
 * begin_checkout / purchase are not here: checkout is not theme-rendered. They come from the
 * "Google Ads conversions" custom pixel (docs/google-ads-pixel.js).
 */
(function () {
  'use strict';

  var cfg = window.__ahGoogleAds;

  function propsObject(p) {
    if (!p) return {};
    if (Array.isArray(p)) {
      var o = {};
      p.forEach(function (kv) { if (kv && kv.key != null) o[kv.key] = kv.value; });
      return o;
    }
    return p;
  }

  /** Merchant Center item id for a cart/checkout line. Same rules as docs/google-ads-pixel.js. */
  function itemIdForLine(line, feedCountry) {
    var props = propsObject(line.properties);
    // A set is reported as the set the feed lists: its AMS slug (`_set_slug`, or a `_set_id` that
    // already is one — '<room>--<set>'). Sets the feed does not list fall through to their pieces.
    if (props._set_slug) return 'SET-' + props._set_slug;
    if (props._set_id && String(props._set_id).indexOf('--') !== -1) return 'SET-' + props._set_id;
    if (props._artwork_id) {
      var sku = String(props._sku || line.sku || '');
      return 'ART-' + props._artwork_id + '-' + (/-PR/.test(sku) ? 'print' : 'framed');
    }
    if (line.product_id && line.variant_id) return 'shopify_' + (feedCountry || 'US') + '_' + line.product_id + '_' + line.variant_id;
    return String(line.variant_id || line.id || '');
  }

  /**
   * Cart lines → gtag items. Set pieces collapse into one SET-<slug> item (the feed lists the
   * set, not its pieces), priced at the sum of its pieces.
   * line: { properties, sku, product_id, variant_id, price (major units), quantity }
   */
  function itemsForLines(lines, feedCountry) {
    var out = [];
    var sets = {};
    (lines || []).forEach(function (line) {
      var id = itemIdForLine(line, feedCountry);
      if (!id) return;
      var qty = Number(line.quantity) || 1;
      var price = Number(line.price) || 0;
      if (id.indexOf('SET-') === 0) {
        if (!sets[id]) { sets[id] = { id: id, price: 0, quantity: qty, google_business_vertical: 'retail' }; out.push(sets[id]); }
        sets[id].price = Math.round((sets[id].price + price) * 100) / 100;
        sets[id].quantity = Math.min(sets[id].quantity, qty);
        return;
      }
      out.push({ id: id, price: price, quantity: qty, google_business_vertical: 'retail' });
    });
    return out;
  }

  function valueOf(items) {
    return Math.round(items.reduce(function (s, i) { return s + i.price * i.quantity; }, 0) * 100) / 100;
  }

  window.ahGoogleAds = { itemIdForLine: itemIdForLine, itemsForLines: itemsForLines };

  if (!cfg || typeof window.gtag !== 'function') return;

  // Marketing consent: Shopify's Customer Privacy API answers true outside consent regions
  // and when the visitor accepted. Absent API → same behaviour as the base gtag config.
  function marketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') return cp.marketingAllowed() !== false;
    } catch (e) { /* fall through */ }
    return true;
  }

  function send(name, params) {
    if (!marketingAllowed()) return;
    try {
      params.send_to = cfg.id;
      window.gtag('event', name, params);
    } catch (e) { /* analytics must never break the storefront */ }
  }

  function handle(evt) {
    if (!evt || !evt.event) return;
    var p = evt.props || {};
    var currency = cfg.currency;
    switch (evt.event) {
      case 'artwork_view': {
        if (!p.item_id) return;
        var price = typeof p.presentment_price === 'number' ? p.presentment_price : undefined;
        send('view_item', {
          value: price,
          currency: currency,
          items: [{ id: p.item_id, price: price, google_business_vertical: 'retail' }]
        });
        return;
      }
      case 'collection_view':
        send('view_item_list', {
          item_list_name: p.collection || '',
          items: (p.item_ids || []).map(function (id) { return { id: id, google_business_vertical: 'retail' }; })
        });
        return;
      case 'search_submit':
        send('view_search_results', { search_term: p.query || '' });
        return;
      case 'cart_lines_added': {
        var items = itemsForLines(p.lines, cfg.feedCountry);
        if (!items.length) return;
        var value = valueOf(items);
        // One hit carries both the remarketing event and the secondary conversion.
        send('add_to_cart', { value: value, currency: currency, items: items });
        if (cfg.addToCart && marketingAllowed()) {
          try { window.gtag('event', 'conversion', { send_to: cfg.addToCart, value: value, currency: currency }); } catch (e) { /* never break */ }
        }
        return;
      }
    }
  }

  var buffer = window.__ahTrackBuffer || [];
  for (var i = 0; i < buffer.length; i++) handle(buffer[i]);
  window.addEventListener('ah:track', function (e) { handle(e.detail); });
})();
